import { ELEVATION_CLIFF_THRESHOLD } from './constants/gameConfig.js';
const SQRT2 = Math.sqrt(2);
class MinHeap {
    data = [];
    get size() { return this.data.length; }
    push(key, f) {
        this.data.push({ key, f });
        this.bubbleUp(this.data.length - 1);
    }
    pop() {
        const top = this.data[0].key;
        const last = this.data.pop();
        if (this.data.length > 0) {
            this.data[0] = last;
            this.sinkDown(0);
        }
        return top;
    }
    bubbleUp(i) {
        while (i > 0) {
            const parent = (i - 1) >> 1;
            if (this.data[i].f >= this.data[parent].f)
                break;
            [this.data[i], this.data[parent]] = [this.data[parent], this.data[i]];
            i = parent;
        }
    }
    sinkDown(i) {
        const n = this.data.length;
        while (true) {
            let smallest = i;
            const l = 2 * i + 1;
            const r = 2 * i + 2;
            if (l < n && this.data[l].f < this.data[smallest].f)
                smallest = l;
            if (r < n && this.data[r].f < this.data[smallest].f)
                smallest = r;
            if (smallest === i)
                break;
            [this.data[i], this.data[smallest]] = [this.data[smallest], this.data[i]];
            i = smallest;
        }
    }
}
const DX = [-1, 0, 1, -1, 1, -1, 0, 1];
const DY = [-1, -1, -1, 0, 0, 1, 1, 1];
const DCOST = [SQRT2, 1, SQRT2, 1, 1, SQRT2, 1, SQRT2];
export class Pathfinder {
    grid;
    constructor(grid) {
        this.grid = grid;
    }
    /**
     * Find a path from (startX, startY) to (endX, endY) in tile coordinates.
     * Returns an array of tile-coordinate waypoints, or null if no path exists.
     * Supports 8-directional movement with diagonal cost √2.
     * maxSearch caps the number of nodes expanded (default 2000) to avoid lag.
     */
    findPath(startX, startY, endX, endY, maxSearch = 2000) {
        const sx = Math.floor(startX);
        const sy = Math.floor(startY);
        const ex = Math.floor(endX);
        const ey = Math.floor(endY);
        if (!this.grid.isWalkable(ex, ey))
            return null;
        if (sx === ex && sy === ey)
            return [{ x: ex, y: ey }];
        const w = this.grid.width;
        const key = (tx, ty) => ty * w + tx;
        const startKey = key(sx, sy);
        const endKey = key(ex, ey);
        const gScore = new Map();
        const cameFrom = new Map();
        const closed = new Set();
        gScore.set(startKey, 0);
        const open = new MinHeap();
        open.push(startKey, this.heuristic(sx, sy, ex, ey));
        let expanded = 0;
        while (open.size > 0 && expanded < maxSearch) {
            const currentKey = open.pop();
            if (currentKey === endKey) {
                return this.reconstructPath(cameFrom, endKey, w);
            }
            if (closed.has(currentKey))
                continue;
            closed.add(currentKey);
            expanded++;
            const cx = currentKey % w;
            const cy = (currentKey - cx) / w;
            const currentG = gScore.get(currentKey);
            for (let d = 0; d < 8; d++) {
                const nx = cx + DX[d];
                const ny = cy + DY[d];
                if (nx < 0 || nx >= this.grid.width || ny < 0 || ny >= this.grid.height)
                    continue;
                if (!this.grid.isWalkable(nx, ny))
                    continue;
                // Elevation cliff check: can't climb steep cliffs (mirrors collidesBoxElevation)
                const srcElev = this.grid.getElevation(cx, cy);
                const dstElev = this.grid.getElevation(nx, ny);
                if (dstElev - srcElev >= ELEVATION_CLIFF_THRESHOLD)
                    continue;
                // Prevent diagonal corner-cutting through solid tiles
                if (DX[d] !== 0 && DY[d] !== 0) {
                    if (!this.grid.isWalkable(cx + DX[d], cy) || !this.grid.isWalkable(cx, cy + DY[d])) {
                        continue;
                    }
                }
                const nk = key(nx, ny);
                if (closed.has(nk))
                    continue;
                const tentativeG = currentG + DCOST[d];
                const prevG = gScore.get(nk);
                if (prevG === undefined || tentativeG < prevG) {
                    gScore.set(nk, tentativeG);
                    cameFrom.set(nk, currentKey);
                    open.push(nk, tentativeG + this.heuristic(nx, ny, ex, ey));
                }
            }
        }
        return null;
    }
    /**
     * Find a path using pixel coordinates. Converts to tile coords internally,
     * returns pixel-center waypoints.
     */
    findPathPixel(startPx, startPy, endPx, endPy, maxSearch = 2000) {
        const ts = this.grid.tileSize;
        const sx = Math.floor(startPx / ts);
        const sy = Math.floor(startPy / ts);
        const ex = Math.floor(endPx / ts);
        const ey = Math.floor(endPy / ts);
        const tilePath = this.findPath(sx, sy, ex, ey, maxSearch);
        if (!tilePath)
            return null;
        const half = ts / 2;
        return tilePath.map(t => ({
            x: t.x * ts + half,
            y: t.y * ts + half,
        }));
    }
    /**
     * Simplify a path by removing collinear intermediate waypoints.
     * Keeps only direction-change points for smoother interpolation.
     */
    static simplifyPath(path) {
        if (path.length <= 2)
            return path;
        const result = [path[0]];
        for (let i = 1; i < path.length - 1; i++) {
            const prev = path[i - 1];
            const curr = path[i];
            const next = path[i + 1];
            const dx1 = Math.sign(curr.x - prev.x);
            const dy1 = Math.sign(curr.y - prev.y);
            const dx2 = Math.sign(next.x - curr.x);
            const dy2 = Math.sign(next.y - curr.y);
            if (dx1 !== dx2 || dy1 !== dy2) {
                result.push(curr);
            }
        }
        result.push(path[path.length - 1]);
        return result;
    }
    heuristic(ax, ay, bx, by) {
        const dx = Math.abs(ax - bx);
        const dy = Math.abs(ay - by);
        return Math.max(dx, dy) + (SQRT2 - 1) * Math.min(dx, dy);
    }
    reconstructPath(cameFrom, endKey, w) {
        const path = [];
        let current = endKey;
        while (current !== undefined) {
            const x = current % w;
            const y = (current - x) / w;
            path.push({ x, y });
            const prev = cameFrom.get(current);
            if (prev === undefined)
                break;
            current = prev;
        }
        path.reverse();
        return path;
    }
}
//# sourceMappingURL=Pathfinder.js.map